import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  IconButton,
  Card,
  CardMedia,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import pagesService from '../../services/pagesService';
import { normalizeSocialLink, normalizeGenericUrl } from '../../utils/socialLinksNormalizer';
import LogoUploadField from './LogoUploadField';
import TemplateSelector from './TemplateSelector';

/**
 * LinkInBioForm - Formulário completo para criar/editar páginas Link in Bio
 * Integrado com PageEditorPage via React Hook Form e props
 * 
 * TODOs implementados:
 * 8.2 - Informações Básicas (title, subtitle, slug)
 * 8.3 - Redes Sociais (Instagram, Twitter, LinkedIn, YouTube, Threads, Facebook)
 * 8.4 - Links Genéricos (até 5 links adicionais)
 * 8.5 - Customização de Estilo (cores, font, tamanho)
 * 8.6 - Validação de Slug em tempo real
 * 8.7 - Seleção de Agenda com opção criar
 */

const LinkInBioForm = ({
  form,
  onSubmit,
  submitting = false,
  templates = [],
  schedules = [],
  isEdit = false,
}) => {
  const { control, watch, formState: { errors }, setValue, trigger } = form;

  const [createScheduleOpen, setCreateScheduleOpen] = useState(false);
  const [newScheduleName, setNewScheduleName] = useState('');
  const [logoUploadError, setLogoUploadError] = useState('');

  // Watch shortId para passar ao componente de upload
  const shortId = watch('shortId');

  // Log agendas carregadas
  useEffect(() => {
    if (schedules?.length > 0) {
      console.log(`[LinkInBioForm] ✓ Agendas carregadas: ${schedules.length}`);
    }
  }, [schedules]);

  // Watch form values for real-time updates
  const currentTitle = watch('title');
  const genericLinks = watch('genericLinks') || [];
  const socialLinks = watch('socialLinks') || {};

  // Auto-generate slug from title (only on creation)
  // URL será slug + id, então slug pode se repetir, é apenas para UX/SEO
  useEffect(() => {
    if (currentTitle && !isEdit) {
      // Normalizar: lowercase, remove acentos, converter espaços em hífens
      const normalized = currentTitle
        .toLowerCase()
        .replace(/[áàâã]/g, 'a')
        .replace(/[éè]/g, 'e')
        .replace(/[íì]/g, 'i')
        .replace(/[óòôõ]/g, 'o')
        .replace(/[úù]/g, 'u')
        .replace(/[ç]/g, 'c')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 50);
      
      if (normalized) {
        setValue('slug', normalized);
      }
    }
  }, [currentTitle, isEdit, setValue]);

  // Social media fields
  const socialMediaFields = [
    { key: 'instagram', label: 'Instagram', placeholder: '@seu_usuario' },
    { key: 'twitter', label: 'Twitter/X', placeholder: '@seu_usuario' },
    { key: 'linkedin', label: 'LinkedIn', placeholder: 'linkedin.com/in/seu_perfil' },
    { key: 'youtube', label: 'YouTube', placeholder: 'youtube.com/@seu_canal' },
    { key: 'threads', label: 'Threads', placeholder: '@seu_usuario' },
    { key: 'facebook', label: 'Facebook', placeholder: 'facebook.com/seu_perfil' },
  ];

  // Add generic link
  const addGenericLink = () => {
    const newLinks = [...(genericLinks || []), { url: '', name: '' }];
    setValue('genericLinks', newLinks);
  };

  // Remove generic link
  const removeGenericLink = (index) => {
    const newLinks = genericLinks.filter((_, i) => i !== index);
    setValue('genericLinks', newLinks);
  };

  // Handle create schedule
  const handleCreateSchedule = () => {
    if (newScheduleName.trim()) {
      // TODO: Integrar com API para criar agenda
      console.log('Criar agenda:', newScheduleName);
      setCreateScheduleOpen(false);
      setNewScheduleName('');
    }
  };

  // Handle social link blur - normalize the URL
  const handleSocialLinkBlur = useCallback((platform, currentValue) => {
    if (currentValue && currentValue.trim()) {
      const normalized = normalizeSocialLink(currentValue, platform);
      if (normalized !== currentValue) {
        // Atualizar o campo se foi normalizado
        setValue(`socialLinks.${platform}.url`, normalized);
        console.log(`[LinkInBioForm] ${platform} normalized:`, { from: currentValue, to: normalized });
      }
    }
  }, [setValue]);

  // Handle generic link blur - normalize the URL
  const handleGenericLinkBlur = useCallback((index, currentValue) => {
    if (currentValue && currentValue.trim()) {
      const normalized = normalizeGenericUrl(currentValue);
      if (normalized !== currentValue) {
        // Atualizar o campo se foi normalizado
        setValue(`genericLinks.${index}.url`, normalized);
        console.log(`[LinkInBioForm] generic link ${index} normalized:`, { from: currentValue, to: normalized });
      }
    }
  }, [setValue]);

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* SEÇÃO 1: INFORMAÇÕES BÁSICAS (TODO 8.2) */}
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Informações Básicas
        </Typography>
        <Grid container spacing={2}>
          {/* Título */}
          <Grid item xs={12}>
            <Controller
              name="title"
              control={control}
              defaultValue=""
              rules={{
                required: 'Título é obrigatório',
                maxLength: { value: 100, message: 'Máximo 100 caracteres' },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Título"
                  placeholder="Ex: Clínica Odontológica Silva"
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  inputProps={{ maxLength: 100 }}
                />
              )}
            />
          </Grid>

          {/* Subtítulo */}
          <Grid item xs={12}>
            <Controller
              name="subtitle"
              control={control}
              defaultValue=""
              rules={{
                maxLength: { value: 3000, message: 'Máximo 3000 caracteres' },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subtítulo"
                  placeholder="Descrição breve sobre seu negócio..."
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.subtitle}
                  helperText={errors.subtitle?.message}
                  inputProps={{ maxLength: 3000 }}
                />
              )}
            />
          </Grid>

        </Grid>
      </Box>

      {/* SEÇÃO 1.5: AVATAR */}
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Avatar
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Configure a imagem do seu perfil
        </Typography>
        <Grid container spacing={3}>
          {/* Logo Upload Field */}
          <Grid item xs={12}>
            <Controller
              name="logoUrl"
              control={control}
              render={({ field }) => (
                <Box>
                  <LogoUploadField
                    logoUrl={field.value}
                    onChange={(url) => {
                      field.onChange(url);
                      setLogoUploadError('');
                    }}
                    pageShortId={shortId || 'preview'}
                    onError={(error) => {
                      console.error('[LinkInBioForm] Logo upload error:', error);
                      setLogoUploadError(error);
                    }}
                    isRound={watch('avatarShape') === 'round'}
                  />
                  {logoUploadError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {logoUploadError}
                    </Alert>
                  )}
                </Box>
              )}
            />
          </Grid>

          {/* Avatar Shape Selector */}
          <Grid item xs={12}>
            <Controller
              name="avatarShape"
              control={control}
              defaultValue="round"
              render={({ field }) => (
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Formato do Avatar
                  </Typography>
                  <RadioGroup
                    {...field}
                    row
                    sx={{ gap: 3 }}
                  >
                    <FormControlLabel
                      value="round"
                      control={<Radio />}
                      label="Circular (Cortado)"
                    />
                    <FormControlLabel
                      value="free"
                      control={<Radio />}
                      label="Proporção Original"
                    />
                  </RadioGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {field.value === 'round'
                      ? 'A imagem será cortada em um círculo para melhor destaque visual'
                      : 'A imagem mantém sua proporção original'}
                  </Typography>
                </FormControl>
              )}
            />
          </Grid>

        </Grid>
      </Box>

      {/* SEÇÃO 2: TEMPLATE */}
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Controller
          name="templateId"
          control={control}
          defaultValue=""
          rules={{ required: 'Template é obrigatório' }}
          render={({ field }) => (
            <TemplateSelector
              templates={templates}
              value={field.value}
              onChange={field.onChange}
              error={errors.templateId?.message}
            />
          )}
        />
      </Box>

      {/* SEÇÃO 3: REDES SOCIAIS (TODO 8.3) */}
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Redes Sociais
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Adicione seus perfis sociais (deixe em branco se não usar)
        </Typography>
        <Grid container spacing={2}>
          {socialMediaFields.map((field) => (
            <Grid item xs={12} sm={6} key={field.key}>
              <Controller
                name={`socialLinks.${field.key}.url`}
                control={control}
                defaultValue=""
                render={({ field: inputField }) => (
                  <TextField
                    {...inputField}
                    label={field.label}
                    placeholder={field.placeholder}
                    fullWidth
                    variant="outlined"
                    size="small"
                    onBlur={() => {
                      inputField.onBlur();
                      handleSocialLinkBlur(field.key, inputField.value);
                    }}
                    helperText="Aceita: @usuario, usuario, ou URL completa"
                  />
                )}
              />
            </Grid>
          ))}
        </Grid>
        <Typography variant="caption" sx={{ color: '#999', mt: 1, display: 'block' }}>
          💡 Dica: Digite apenas @usuario, usuario, ou a URL completa - o sistema fará a formatação automática
        </Typography>
      </Box>

      {/* SEÇÃO 4: LINKS GENÉRICOS (TODO 8.4) */}
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Links Adicionais
          </Typography>
          {genericLinks.length < 5 && (
            <Button
              startIcon={<AddIcon />}
              onClick={addGenericLink}
              size="small"
              variant="outlined"
              disabled={submitting}
            >
              Adicionar Link
            </Button>
          )}
        </Box>
        {genericLinks.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#999' }}>
            Nenhum link adicional. Clique em "Adicionar Link" para criar um.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {genericLinks.map((link, index) => (
              <Grid item xs={12} key={index}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Controller
                      name={`genericLinks.${index}.name`}
                      control={control}
                      defaultValue={link.name || ''}
                      render={({ field: inputField }) => (
                        <TextField
                          {...inputField}
                          label="Nome"
                          placeholder="Ex: Meu Site"
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                    <Controller
                      name={`genericLinks.${index}.url`}
                      control={control}
                      defaultValue={link.url || ''}
                      render={({ field: inputField }) => (
                        <TextField
                          {...inputField}
                          label="URL"
                          placeholder="Ex: https://meusite.com ou meusite.com"
                          fullWidth
                          size="small"
                          onBlur={() => {
                            inputField.onBlur();
                            handleGenericLinkBlur(index, inputField.value);
                          }}
                        />
                      )}
                    />
                  </Box>
                  <IconButton
                    onClick={() => removeGenericLink(index)}
                    color="error"
                    size="small"
                    sx={{ mt: 1 }}
                    disabled={submitting}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* SEÇÃO 5: CUSTOMIZAÇÃO DE ESTILO (TODO 8.5) */}
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Customização de Estilo
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setValue('customStyles.backgroundColor', '#ffffff');
              setValue('customStyles.textColor', '#000000');
              setValue('customStyles.linkColor', '#1976d2');
              setValue('customStyles.linkBackground', '#f0f0f0');
              setValue('customStyles.fontFamily', 'Arial');
              setValue('customStyles.fontSize', 'medium');
            }}
            disabled={submitting}
          >
            Resetar Estilos
          </Button>
        </Box>
        <Grid container spacing={2}>
          {/* Cor de Fundo */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="customStyles.backgroundColor"
              control={control}
              defaultValue="#ffffff"
              render={({ field: inputField }) => (
                <TextField
                  {...inputField}
                  label="Cor de Fundo"
                  type="color"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          {/* Cor do Texto */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="customStyles.textColor"
              control={control}
              defaultValue="#000000"
              render={({ field: inputField }) => (
                <TextField
                  {...inputField}
                  label="Cor do Texto"
                  type="color"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          {/* Cor dos Links */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="customStyles.linkColor"
              control={control}
              defaultValue="#1976d2"
              render={({ field: inputField }) => (
                <TextField
                  {...inputField}
                  label="Cor dos Links"
                  type="color"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          {/* Cor de Fundo dos Links */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="customStyles.linkBackground"
              control={control}
              defaultValue="#f0f0f0"
              render={({ field: inputField }) => (
                <TextField
                  {...inputField}
                  label="Fundo dos Links"
                  type="color"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          {/* Cor do Card */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="customStyles.cardBackground"
              control={control}
              defaultValue="#ffffff"
              render={({ field: inputField }) => (
                <TextField
                  {...inputField}
                  label="Fundo do Card"
                  type="color"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          {/* Família de Fonte */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="customStyles.fontFamily"
              control={control}
              defaultValue="Arial"
              render={({ field: inputField }) => (
                <FormControl fullWidth>
                  <InputLabel>Família de Fonte</InputLabel>
                  <Select {...inputField} label="Família de Fonte">
                    <MenuItem value="Arial">Arial (Limpo)</MenuItem>
                    <MenuItem value="Helvetica">Helvetica (Moderno)</MenuItem>
                    <MenuItem value="Georgia">Georgia (Clássico)</MenuItem>
                    <MenuItem value="Verdana">Verdana (Legível)</MenuItem>
                    <MenuItem value="Times New Roman">Times New Roman (Formal)</MenuItem>
                    <MenuItem value="Courier New">Courier New (Typewriter)</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Tamanho da Fonte */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="customStyles.fontSize"
              control={control}
              defaultValue="medium"
              render={({ field: inputField }) => (
                <FormControl fullWidth>
                  <InputLabel>Tamanho da Fonte</InputLabel>
                  <Select {...inputField} label="Tamanho da Fonte">
                    <MenuItem value="small">Pequeno</MenuItem>
                    <MenuItem value="medium">Médio</MenuItem>
                    <MenuItem value="large">Grande</MenuItem>
                    <MenuItem value="extra-large">Muito Grande</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Box>

      {/* SEÇÃO 6: AGENDA (TODO 8.7) */}
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Agenda Associada
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Controller
              name="scheduleId"
              control={control}
              defaultValue=""
              rules={{ required: 'Selecione uma agenda ou crie uma nova' }}
              render={({ field: inputField }) => (
                <FormControl fullWidth error={!!errors.scheduleId}>
                  <InputLabel>Agenda</InputLabel>
                  <Select 
                    {...inputField} 
                    label="Agenda"
                    onChange={(e) => {
                      inputField.onChange(e);
                      // Preencher automaticamente o nome do link com o nome da agenda
                      const selectedSchedule = schedules.find(s => s.id === e.target.value);
                      if (selectedSchedule) {
                        if (!watch('agendaLinkName')) {
                          setValue('agendaLinkName', selectedSchedule.name);
                        }
                        // Sempre preencher scheduleName com o nome da agenda selecionada
                        setValue('scheduleName', selectedSchedule.name);
                      }
                    }}
                  >
                    {schedules.map((schedule) => (
                      <MenuItem key={schedule.id} value={schedule.id}>
                        {schedule.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
          <Button
            variant="outlined"
            onClick={() => setCreateScheduleOpen(true)}
            sx={{ mt: 1 }}
            disabled={submitting}
          >
            + Criar
          </Button>
        </Box>
        
        {/* Nome do link da agenda (opcional) */}
        <Controller
          name="agendaLinkName"
          control={control}
          defaultValue=""
          render={({ field: inputField }) => (
            <TextField
              {...inputField}
              label="Nome do Botão de Agendamento"
              placeholder="Ex: Agendar Consulta, Marcar Horário..."
              fullWidth
              size="small"
              helperText="Texto que aparece no botão. Se vazio, usa o nome da agenda."
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      </Box>

      {/* SEÇÃO 7: URL DA PÁGINA (AUTO-GERADO DO TÍTULO) */}
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          URL da Página
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          URL amigável para sua página (auto-gerada do título, pode editar)
        </Typography>

        {/* Slug dentro da URL */}
        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1, fontWeight: 600 }}>
          Slug (URL amigável)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1, border: '1px solid #eee' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666', whiteSpace: 'nowrap' }}>
            {(import.meta.env.VITE_PAGES_URL || 'https://pages.agendario.com.br').replace('https://', '')}/
          </Typography>
          <Controller
            name="slug"
            control={control}
            defaultValue=""
            rules={{
              required: 'Slug é obrigatório',
              pattern: {
                value: /^[a-z0-9-]+$/,
                message: 'Use apenas letras minúsculas, números e hífens',
              },
              maxLength: { value: 50, message: 'Máximo 50 caracteres' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="seu-slug"
                variant="standard"
                size="small"
                error={!!errors.slug}
                inputProps={{ maxLength: 50, style: { fontFamily: 'monospace', fontSize: '0.9rem' } }}
                disabled={submitting}
                sx={{
                  flex: 1,
                  '& .MuiInput-underline:before': { borderBottomColor: '#ddd' },
                  '& .MuiInput-underline:hover:before': { borderBottomColor: '#999' },
                }}
              />
            )}
          />
          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666', whiteSpace: 'nowrap' }}>
            {isEdit ? `-${watch('shortId')}` : '-(codigo dinamico)'}
          </Typography>
        </Box>
        {errors.slug && (
          <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mb: 2 }}>
            {errors.slug.message}
          </Typography>
        )}
        {!isEdit && (
          <>
            <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 2 }}>
              Auto-preenchido do título, mas você pode editar
            </Typography>
            <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 3 }}>
              O código dinâmico será gerado automaticamente após criar a página.
            </Typography>
          </>
        )}
      </Box>

      {/* Divider */}
      <Divider />

      {/* Submit Button */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={submitting}
          sx={{ minWidth: 150 }}
        >
          {submitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {isEdit ? 'Atualizar Página' : 'Criar Página'}
        </Button>
      </Box>

      {/* Dialog para criar agenda */}
      <Dialog open={createScheduleOpen} onClose={() => setCreateScheduleOpen(false)}>
        <DialogTitle>Criar Nova Agenda</DialogTitle>
        <DialogContent sx={{ minWidth: 400, mt: 2 }}>
          <TextField
            autoFocus
            label="Nome da Agenda"
            fullWidth
            value={newScheduleName}
            onChange={(e) => setNewScheduleName(e.target.value)}
            placeholder="Ex: Agendas do Consultório"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateScheduleOpen(false)}>Cancelar</Button>
          <Button onClick={handleCreateSchedule} variant="contained">
            Criar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LinkInBioForm;
